import Button from 'components/Forms/Button/Button';
import Form from 'components/Forms/Form/Form';
import FormLineError from 'components/Forms/Lib/FormLineError/FormLineError';
import TextInput from 'components/Forms/TextInput/TextInput';
import { Webline } from 'components/Layout/Webline/Webline';
import {
    useConfiguratorDrawerSizesForm,
    useConfiguratorDrawerSizesFormMeta,
} from 'components/Pages/Configurator/configuratorDrawerSizesFormMeta';
import { AddToCartButton } from 'components/Pages/Configurator/Popup/AddToCart/AddToCartButton';
import ShowProductListButton from 'components/Pages/Configurator/Popup/ProductList/ShowProductListButton';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { SIZES_OF_CELL_IN_CM } from 'helpers/configurator/configuratorConstants';
import { calculateDrawerPaddings } from 'helpers/configurator/configuratorHelpers';
import useTranslation from 'next-translate/useTranslation';
import { Controller, FormProvider, SubmitHandler } from 'react-hook-form';
import { usePersistStore } from 'store/usePersistStore';
import { ConfiguratorDrawerSizesType } from 'types/configurator';
import { ConfiguratorDrawerSizesFormType } from 'types/form';

export const ConfiguratorDrawerSizesForm: FC = () => {
    const { t } = useTranslation();
    const { updateConfiguratorState } = usePersistStore();
    const [formProviderMethods] = useConfiguratorDrawerSizesForm();
    const { fields, formName } = useConfiguratorDrawerSizesFormMeta(formProviderMethods);
    const { type: domainType } = useDomainConfig();

    const onSubmitHandler: SubmitHandler<ConfiguratorDrawerSizesFormType> = (formValues) => {
        const drawerSizesWithoutRounding: ConfiguratorDrawerSizesType = {
            length: formValues.length / SIZES_OF_CELL_IN_CM.length,
            width: formValues.width / SIZES_OF_CELL_IN_CM.width,
        };

        updateConfiguratorState({
            drawerSizes: {
                length: Math.floor(drawerSizesWithoutRounding.length),
                width: Math.floor(drawerSizesWithoutRounding.width),
            },
            drawerPaddings: calculateDrawerPaddings(drawerSizesWithoutRounding),
            productsInDrawer: [],
        });
    };

    return (
        <Webline className="pb-8">
            <div className="rounded-md bg-grayLight p-4">
                <div className="mb-2">{t('Enter internal dimensions of the drawer')}</div>
                <FormProvider {...formProviderMethods}>
                    <Form
                        className="flex flex-col items-start justify-start gap-x-8 gap-y-2 lg:flex-row lg:items-center"
                        onSubmit={formProviderMethods.handleSubmit(onSubmitHandler)}
                    >
                        <div className="flex gap-4">
                            <Controller
                                name={fields.width.name}
                                render={({ fieldState: { error }, field }) => (
                                    <div className="flex flex-col">
                                        <TextInput
                                            required
                                            fieldRef={field}
                                            id={formName + '-' + fields.width.name}
                                            label={fields.width.label}
                                            name={fields.width.name}
                                            type="text"
                                        />
                                        <FormLineError
                                            data-testid={formName + '-' + fields.width.name + '-error'}
                                            error={error}
                                            inputType="text-input"
                                            textInputSize="small"
                                        />
                                    </div>
                                )}
                            />
                            <Controller
                                name={fields.length.name}
                                render={({ fieldState: { error }, field }) => (
                                    <div className="flex flex-col">
                                        <TextInput
                                            required
                                            fieldRef={field}
                                            id={formName + '-' + fields.length.name}
                                            label={fields.length.label}
                                            name={fields.length.name}
                                            type="text"
                                        />
                                        <FormLineError
                                            data-testid={formName + '-' + fields.length.name + '-error'}
                                            error={error}
                                            inputType="text-input"
                                            textInputSize="small"
                                        />
                                    </div>
                                )}
                            />
                        </div>
                        <Button className="h-fit w-fit whitespace-nowrap vl:text-h6" size="small" type="submit">
                            {t('Change dimensions')}
                        </Button>
                        <ShowProductListButton />
                        {domainType !== 'web' && <AddToCartButton />}
                    </Form>
                </FormProvider>
            </div>
        </Webline>
    );
};
